/**
 * Site mode and language detection.
 *
 * The customer-facing "CDN mode" is driven by the hostname: traffic served
 * from discover.indigo.ca (English) or lireplus.indigo.ca (French) via
 * Akamai. The domain IS the language; customers switch languages by going
 * to the sister domain.
 *
 * Local testing overrides:
 *   - ?cdn=1 query param or REACT_APP_FORCE_CDN_MODE=true -> CDN mode
 *   - ?lang=fr query param -> French
 */

export type Language = 'en' | 'fr';

const CDN_HOSTNAME_LANGUAGES: Record<string, Language> = {
  'discover.indigo.ca': 'en',
  'lireplus.indigo.ca': 'fr',
};

const SISTER_HOSTNAMES: Record<string, string> = {
  'discover.indigo.ca': 'lireplus.indigo.ca',
  'lireplus.indigo.ca': 'discover.indigo.ca',
};

const hostname = (): string => globalThis.location.hostname.toLowerCase();

const searchParams = (): URLSearchParams => new URLSearchParams(globalThis.location.search);

/** True when running on a real customer-facing domain (not a local override). */
export const isRealCdnHost = (): boolean => hostname() in CDN_HOSTNAME_LANGUAGES;

/** True when the customer-facing (CDN) experience should be shown. */
export const isCdnMode = (): boolean => {
  if (isRealCdnHost()) return true;
  if (process.env.REACT_APP_FORCE_CDN_MODE === 'true') return true;
  return searchParams().get('cdn') === '1';
};

/**
 * Headers that align the backend with the frontend CDN experience.
 *
 * Akamai injects these on production CDN traffic. For local overrides
 * (?cdn=1, REACT_APP_FORCE_CDN_MODE), the SPA must send them so
 * resolve_site_mode() enters CDN mode while EDGE_AUTH_SECRET is unset.
 */
export const getCdnSiteHeaders = (): Record<string, string> => {
  if (!isCdnMode()) return {};
  return {
    'X-Indigo-Site': 'cdn',
    'X-Indigo-Lang': getLanguage(),
  };
};

/** Language of the current experience ('fr' only on lireplus / via override). */
export const getLanguage = (): Language => {
  const hostLanguage = CDN_HOSTNAME_LANGUAGES[hostname()];
  if (hostLanguage) return hostLanguage;
  return searchParams().get('lang') === 'fr' ? 'fr' : 'en';
};

/**
 * URL of the same page in the other language.
 *
 * On the real domains this maps to the sister domain, preserving the path.
 * In local/override mode it toggles the ?lang query param instead.
 */
export const getLanguageSwitchUrl = (): string => {
  const sister = SISTER_HOSTNAMES[hostname()];
  if (sister) {
    return `${globalThis.location.protocol}//${sister}${globalThis.location.pathname}`;
  }
  const params = searchParams();
  if (getLanguage() === 'fr') {
    params.delete('lang');
  } else {
    params.set('lang', 'fr');
  }
  const query = params.toString();
  const queryString = query ? `?${query}` : '';
  return `${globalThis.location.pathname}${queryString}`;
};

/**
 * Home URL of the current experience.
 *
 * The domain carries the language, so staying on the current origin is what
 * keeps a French shopper on the French site. Local overrides live in the query
 * string instead, and have to be carried across by hand.
 */
export const getHomeUrl = (): string => {
  if (isRealCdnHost()) return `${globalThis.location.protocol}//${hostname()}/`;
  const current = searchParams();
  const carried = new URLSearchParams();
  for (const key of ['cdn', 'lang']) {
    const value = current.get(key);
    if (value) carried.set(key, value);
  }
  const query = carried.toString();
  return query ? `/?${query}` : '/';
};

/**
 * API base URL.
 *
 * - Real CDN domains: /api/* is routed first-party through Akamai to the
 *   backend (cookies stay same-origin).
 * - Internal production: same-origin /api/* — the frontend's nginx proxies
 *   to the backend and injects the API key server-side, so the key never
 *   ships in the JS bundle.
 * - Local development (npm start): direct backend URL via REACT_APP_API_URL.
 */
export const getApiBaseUrl = (): string => {
  if (isRealCdnHost()) return '/api';
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  return process.env.NODE_ENV === 'development' ? 'http://localhost:5050/api' : '/api';
};

/**
 * Product detail page (PDP) redirect configuration.
 *
 * On the customer-facing CDN experience a recommendation click sends the shopper
 * to the Indigo storefront product page in a new tab instead of opening the
 * in-app details modal. Both the destination and the toggle are env-configurable
 * so the domain can change per environment and the behavior can be turned off
 * without a code change.
 *
 *   - REACT_APP_PDP_BASE_URL       base URL the book identifier is appended to
 *   - REACT_APP_PDP_REDIRECT_ENABLED  set to 'false' to disable the redirect
 */
const DEFAULT_PDP_BASE_URL = 'https://pdp.indigoimages.ca/product/';

const getPdpBaseUrl = (): string => {
  const configured = process.env.REACT_APP_PDP_BASE_URL || DEFAULT_PDP_BASE_URL;
  return configured.endsWith('/') ? configured : `${configured}/`;
};

/**
 * True when a recommendation click should redirect to the storefront PDP.
 *
 * Gated on CDN mode: the internal testing tool keeps the in-app details modal.
 */
export const isPdpRedirectEnabled = (): boolean => {
  if (!isCdnMode()) return false;
  return process.env.REACT_APP_PDP_REDIRECT_ENABLED !== 'false';
};

/**
 * Storefront PDP URL for a book identifier (ISBN/SKU/book_id as resolved by the UI).
 */
export const getPdpUrl = (bookId: string): string => {
  const cleanId = encodeURIComponent(bookId.replace(/-/g, ''));
  return `${getPdpBaseUrl()}${cleanId}`;
};
